import produce, { Draft } from 'immer';

import { Dictionary } from 'lodash';
import { ISetManagementIssuedOrder } from '../../actions/billing';
import {
  IPatchUserData,
  ISetArchiveTimeRange,
  ISetNews,
  ISetNewsError,
  ISetNewsFetching,
  ISetRateLimitedOgcCount,
  ISetRateLimitedOgcCountFetching,
  ISetRateLimitedOgcError,
  ISetStatistics,
  ISetStatisticsError,
  ISetStatisticsFetching,
  ISetStatisticsTimeRange,
  ISetTpdiQuota,
  ISetTpdiQuotaError,
  ISetTpdiQuotaFetching,
  ISetUserData,
  ISetUserDataError,
  ISetUserDataFetching,
  ISetUsingArchiveTimeRange,
  QuotaPair,
} from '../../actions/dashboard';
import * as actionTypes from '../../constants/actionTypes';
import { Cache } from '../../meta/types/requests-statistics';
import { AVAILABLE_TIME_RANGES } from '../../modules/Dashboard/RequestStatistics/TimeRangePicker';
import { NewsEntry } from '../../meta/types/news';
import { UserData } from '../../meta/types/userdata';

const currentDate = new Date();

export const initialState = Object.freeze({
  fetchingRateLimitedCount: false,
  statisticsCache: {
    rateLimitedRequestsCount: {},
    statistics: {},
  } as Cache,
  selectedTimeRange: AVAILABLE_TIME_RANGES[2],
  archiveTimeRange: {
    month: currentDate.getMonth().toString(),
    year: currentDate.getFullYear().toString(),
    isSelected: false,
  },
  fetchingStatistics: false,
  fetchingNews: false,
  fetchingUserData: false,
  fetchingTpdiQuotas: false,
});

export type DashboardState = typeof initialState & {
  rateLimitedRequestsError?: string;
  statisticsError?: string;
  news?: NewsEntry[];
  newsError?: string;
  userData?: UserData;
  userDataError?: string;
  tpdiQuotas?: Dictionary<QuotaPair>;
  tpdiQuotasError?: string;
};

type DashboardAction =
  | ISetRateLimitedOgcCount
  | ISetRateLimitedOgcCountFetching
  | ISetRateLimitedOgcError
  | ISetStatisticsTimeRange
  | ISetArchiveTimeRange
  | ISetUsingArchiveTimeRange
  | ISetManagementIssuedOrder
  | ISetStatistics
  | ISetStatisticsFetching
  | ISetStatisticsError
  | ISetTpdiQuota
  | ISetTpdiQuotaFetching
  | ISetTpdiQuotaError
  | ISetNews
  | ISetNewsFetching
  | ISetNewsError
  | ISetUserData
  | ISetUserDataFetching
  | ISetUserDataError
  | IPatchUserData;

const dashboard = (state: DashboardState = initialState, action: DashboardAction) => {
  return produce<DashboardState>(state, (draft: Draft<DashboardState>) => {
    switch (action.type) {
      case actionTypes.SET_STATISTICS_ERROR:
        draft.fetchingStatistics = false;
        draft.statisticsError = action.message;
        return;
      case actionTypes.SET_STATISTICS_FETCHING:
        draft.fetchingStatistics = true;
        return;
      case actionTypes.SET_STATISTICS:
        draft.statisticsCache.statistics[action.timeRange.value] = action.cost;
        draft.fetchingStatistics = false;
        return;
      case actionTypes.SET_TPDI_QUOTA_ERROR:
        draft.fetchingTpdiQuotas = false;
        draft.tpdiQuotasError = action.message;
        return;
      case actionTypes.SET_TPDI_QUOTA_FETCHING:
        draft.fetchingTpdiQuotas = true;
        return;
      case actionTypes.SET_TPDI_QUOTA:
        draft.tpdiQuotas = action.quotas;
        draft.fetchingTpdiQuotas = false;
        return;
      case actionTypes.SET_RATE_LIMITED_REQUESTS_ERROR:
        draft.rateLimitedRequestsError = action.message;
        draft.fetchingRateLimitedCount = false;
        return;
      case actionTypes.SET_STATISTICS_TIME_RANGE:
        draft.selectedTimeRange = action.timeRange;
        return;
      case actionTypes.SET_ARCHIVE_TIME_RANGE:
        draft.archiveTimeRange.month =
          action.archiveTimeRange.month ?? draft.archiveTimeRange.month;
        draft.archiveTimeRange.year = action.archiveTimeRange.year ?? draft.archiveTimeRange.year;
        return;
      case actionTypes.SET_SELECT_ARCHIVE_TIME_RANGE:
        draft.archiveTimeRange.isSelected = action.isUsingArchive;
        return;
      case actionTypes.SET_RATE_LIMITED_REQUESTS_COUNT:
        draft.fetchingRateLimitedCount = false;
        draft.statisticsCache.rateLimitedRequestsCount[action.timeRange.value] = action.count;
        return;
      case actionTypes.SET_RATE_LIMITED_REQUESTS_COUNT_FETCHING:
        draft.rateLimitedRequestsError = undefined;
        draft.fetchingRateLimitedCount = action.fetching;
        return;
      case actionTypes.SET_NEWS_FETCHING:
        draft.fetchingNews = true;
        return;
      case actionTypes.SET_NEWS_ERROR:
        draft.fetchingNews = false;
        draft.newsError = action.message;
        return;
      case actionTypes.SET_NEWS:
        draft.fetchingNews = false;
        draft.news = action.news;
        return;
      case actionTypes.SET_USER_DATA_FETCHING:
        draft.fetchingUserData = true;
        return;
      case actionTypes.SET_USER_DATA_ERROR:
        draft.fetchingUserData = false;
        draft.userDataError = action.message;
        return;
      case actionTypes.SET_USER_DATA:
        draft.fetchingUserData = false;
        draft.userData = action.userData;
        return;
      case actionTypes.PATCH_USER_DATA:
        draft.userData = {
          ...draft.userData,
          ...action.userData,
        };
        return;
      default:
        return;
    }
  });
};

export default dashboard;
